// Phase COTTON-RM-002 Part B — Bale Traceability.
//
// One complete history page for a single bale. Pulls together identity,
// QR, GRN/PO/DO/supplier links, weight, QC, costs, scan history,
// movement entries, and downstream issue/laydown/production records.
// Reads only — no new data entry.

import {
  Alert, Card, Col, Descriptions, Empty, Result, Row, Skeleton, Space, Tabs,
  Tag, Timeline, Typography,
} from 'antd'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import apiClient from '@/shared/api/client'
import { cottonRmApi } from '@/features/cotton-rm/cottonRmApi'

const { Text, Title } = Typography

interface BaleDetail {
  id: string
  bale_no: string
  grn_id: string | null
  weight_kg: number
  lot_no: string | null
  grade: string | null
  godown: string | null
  location: string
  status: string
  qc_status: string
  bale_stage: string
  godown_allocation_status: string | null
  qr_code_value: string | null
  qr_status: string
  qr_printed_at: string | null
  qr_last_scanned_at: string | null
  qr_print_count: number
  barcode: string | null
  moisture_pct: number | null
  micronaire: number | null
  uhml_mm: number | null
  uniformity_index: number | null
  strength_g_tex: number | null
  trash_pct: number | null
  rd_value: number | null
  plus_b: number | null
  effective_cost_per_kg: number | null
  cotton_variety: string | null
  crop_year: string | null
  origin: string | null
  test_id: string | null
  test_date: string | null
  qc_remarks: string | null
}

function val<T>(v: T | null | undefined, fmt?: (v: T) => string): React.ReactNode {
  if (v === null || v === undefined || v === '') return <Tag>DATA_NOT_AVAILABLE</Tag>
  return fmt ? fmt(v as T) : String(v)
}

export default function BaleTraceabilityPage() {
  const { baleId } = useParams<{ baleId: string }>()
  const navigate = useNavigate()

  const { data: bale, isLoading, error } = useQuery({
    queryKey: ['bale-traceability', baleId],
    queryFn: () => apiClient.get<BaleDetail>(`/bales/${baleId}/journey`).then(r => r.data),
    enabled: !!baleId,
  })
  const { data: statusView } = useQuery({
    queryKey: ['bale-status-view', baleId],
    queryFn: () => cottonRmApi.baleStatus(baleId!),
    enabled: !!baleId,
  })
  const { data: scans = [] } = useQuery({
    queryKey: ['bale-scans', baleId],
    queryFn: () => cottonRmApi.scanHistory(baleId!, 100),
    enabled: !!baleId,
  })
  const { data: readiness } = useQuery({
    queryKey: ['bale-readiness', baleId],
    queryFn: () => cottonRmApi.baleReadiness(baleId!),
    enabled: !!baleId,
  })
  const { data: adjustments = [] } = useQuery({
    queryKey: ['bale-grn-adj', bale?.grn_id],
    queryFn: () => cottonRmApi.listAdjForGrn(bale!.grn_id!),
    enabled: !!bale?.grn_id,
  })

  if (isLoading) return <Card style={{ margin: 16 }}><Skeleton active /></Card>
  if (error || !bale) {
    return (
      <Result status="404"
              title="Bale not found"
              extra={<a onClick={() => navigate(-1)}>Back</a>} />
    )
  }

  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>
        Bale {bale.bale_no}
      </Title>
      <Space wrap style={{ marginBottom: 12 }}>
        {bale.qr_code_value
          ? <Tag color="blue">QR: {bale.qr_code_value}</Tag>
          : <Tag color="warning">QR NOT GENERATED</Tag>}
        {bale.barcode && <Tag>Barcode: {bale.barcode}</Tag>}
        <Tag color="default">Stage: {bale.bale_stage}</Tag>
        <Tag color={bale.qc_status === 'APPROVED' ? 'success'
                      : bale.qc_status === 'HOLD' ? 'warning'
                      : bale.qc_status === 'REJECTED' ? 'error'
                      : 'default'}>QC: {bale.qc_status}</Tag>
        <Tag>Status: {bale.status}</Tag>
        {readiness && (
          <Tag color={readiness.ready ? 'success' : 'warning'}>
            {readiness.ready ? 'READY FOR MIXING' : 'BLOCKED'}
          </Tag>
        )}
      </Space>

      <Tabs defaultActiveKey="identity" items={[
        { key: 'identity', label: 'Identity', children: (
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <Card title="Identification" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Bale #">{bale.bale_no}</Descriptions.Item>
                  <Descriptions.Item label="QR">
                    {val(bale.qr_code_value, v => v)}
                  </Descriptions.Item>
                  <Descriptions.Item label="QR status">
                    <Tag>{bale.qr_status}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Legacy barcode">
                    {val(bale.barcode)}
                  </Descriptions.Item>
                  <Descriptions.Item label="GRN">
                    {val(bale.grn_id)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Lot">
                    {val(bale.lot_no)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Grade">
                    {val(bale.grade)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Location" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Godown">
                    {val(bale.godown)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Location string">
                    {val(bale.location)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Allocation status">
                    {val(bale.godown_allocation_status)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Last scanned">
                    {val(bale.qr_last_scanned_at,
                          (s: string) => new Date(s).toLocaleString())}
                  </Descriptions.Item>
                  <Descriptions.Item label="Print count">
                    {bale.qr_print_count ?? 0}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            {statusView && (
              <Col xs={24}>
                <Card title="3-Dimension Status (derived)" size="small">
                  <Row>
                    <Col span={8}><Text type="secondary">Physical</Text>
                      <div><Tag color="blue">{statusView.dimensions.physical_status}</Tag></div></Col>
                    <Col span={8}><Text type="secondary">QC</Text>
                      <div><Tag color="purple">{statusView.dimensions.qc_status}</Tag></div></Col>
                    <Col span={8}><Text type="secondary">Commercial</Text>
                      <div><Tag color="cyan">{statusView.dimensions.commercial_status}</Tag></div></Col>
                  </Row>
                </Card>
              </Col>
            )}
          </Row>
        ) },

        { key: 'quality', label: 'Quality (Moisture + HVI)', children: (
          <Card>
            <Descriptions bordered column={3} size="small">
              <Descriptions.Item label="Moisture %">
                {val(bale.moisture_pct, (v: number) => `${v}%`)}
              </Descriptions.Item>
              <Descriptions.Item label="Micronaire">
                {val(bale.micronaire, (v: number) => String(v))}
              </Descriptions.Item>
              <Descriptions.Item label="UHML">
                {val(bale.uhml_mm, (v: number) => `${v} mm`)}
              </Descriptions.Item>
              <Descriptions.Item label="Strength">
                {val(bale.strength_g_tex, (v: number) => `${v} g/tex`)}
              </Descriptions.Item>
              <Descriptions.Item label="Uniformity Index">
                {val(bale.uniformity_index, (v: number) => `${v}%`)}
              </Descriptions.Item>
              <Descriptions.Item label="Trash">
                {val(bale.trash_pct, (v: number) => `${v}%`)}
              </Descriptions.Item>
              <Descriptions.Item label="Rd">
                {val(bale.rd_value)}
              </Descriptions.Item>
              <Descriptions.Item label="+b">
                {val(bale.plus_b)}
              </Descriptions.Item>
              <Descriptions.Item label="Test date">
                {val(bale.test_date)}
              </Descriptions.Item>
              <Descriptions.Item label="QC remarks" span={3}>
                {val(bale.qc_remarks)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) },

        { key: 'cost', label: 'Cost', children: (
          <>
            <Card>
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Weight">{bale.weight_kg} kg</Descriptions.Item>
                <Descriptions.Item label="Effective ₹/kg">
                  {val(bale.effective_cost_per_kg, (v: number) => `₹${v}`)}
                </Descriptions.Item>
                <Descriptions.Item label="Cotton variety">
                  {val(bale.cotton_variety)}
                </Descriptions.Item>
                <Descriptions.Item label="Crop year">
                  {val(bale.crop_year)}
                </Descriptions.Item>
                <Descriptions.Item label="Origin">
                  {val(bale.origin)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
            <Card title={`Cost adjustments on this GRN (${adjustments.length})`}
                   style={{ marginTop: 12 }}>
              {adjustments.length === 0
                ? <Empty description="No cost adjustments on this GRN." />
                : (
                  <Timeline mode="left"
                            items={adjustments.map(a => ({
                              color: a.approval_status === 'APPROVED' ? 'green'
                                     : a.approval_status === 'PENDING' ? 'blue' : 'red',
                              label: new Date(a.created_at).toLocaleString(),
                              children: (
                                <Space direction="vertical" size={2}>
                                  <Text strong>{a.adjustment_type} ·
                                    ₹{(a.amount * a.sign).toLocaleString()}</Text>
                                  <Tag color={a.approval_status === 'APPROVED' ? 'success' : 'default'}>
                                    {a.approval_status}
                                  </Tag>
                                  <Text type="secondary">{a.reason}</Text>
                                  {a.downstream_warning && (
                                    <Alert type="warning" message={a.downstream_warning} />
                                  )}
                                </Space>
                              ),
                            }))} />
                )}
            </Card>
          </>
        ) },

        { key: 'readiness', label: 'Mixing Readiness', children: (
          <Card>
            {!readiness ? <Skeleton active /> : (
              readiness.ready
                ? <Result status="success"
                          title="Ready for mixing"
                          subTitle="All gates clear: located, QC approved, cost set." />
                : (
                  <Alert type="warning" showIcon
                          message="Blocked from mixing"
                          description={
                            <ul style={{ marginBottom: 0 }}>
                              {readiness.blocking_reasons.map(r => (
                                <li key={r}><code>{r}</code></li>
                              ))}
                            </ul>
                          } />
                )
            )}
          </Card>
        ) },

        { key: 'scans', label: `QR Scan History (${scans.length})`, children: (
          <Card>
            {scans.length === 0
              ? <Empty description="No QR scans recorded yet." />
              : (
                <Timeline mode="left"
                          items={scans.map(s => ({
                            color: s.result_status === 'ALLOWED' ? 'green'
                                  : s.result_status === 'BLOCKED' ? 'red'
                                  : s.result_status === 'WARNING' ? 'orange'
                                  : 'blue',
                            label: new Date(s.scan_datetime).toLocaleString(),
                            children: (
                              <Space direction="vertical" size={2}>
                                <Tag color={s.result_status === 'ALLOWED' ? 'success'
                                            : s.result_status === 'BLOCKED' ? 'error'
                                            : 'warning'}>
                                  {s.result_status}
                                </Tag>
                                <Text>{s.scan_context}</Text>
                                {s.error_message && (
                                  <Text type="danger">{s.error_message}</Text>
                                )}
                              </Space>
                            ),
                          }))} />
              )}
          </Card>
        ) },
      ]} />
    </div>
  )
}
